// Sugalarm — synthetic Now-screen preview state (cycle 36).
//
// Single source of truth for "is the user previewing a zone right now,
// and which one." Replaces the cycle-15 / cycle-29 sequencer that
// auto-rotated through every alert sound on a 12-second loop. The new
// model is on-demand: a button in Settings → Alerts → Test alarms
// calls `startPreview(zone, sgv)` and the preview overlay paints a
// full synthetic Now-screen for 5 seconds before auto-clearing.
//
// Local-only. No localStorage (preview is ephemeral — closing the tab
// kills it); no API; no WebSocket broadcast. The preview is tab-local
// by design.

import type { Thresholds } from "./types";

const EVENT = "sugalarm:alarm-preview-change";
const DURATION_MS = 5000;

export type PreviewZone =
  | "urgent_low"
  | "low"
  | "in_range"
  | "high"
  | "urgent_high";

export interface PreviewState {
  /** Active preview zone, or null when no preview is running. */
  zone: PreviewZone | null;
  /** Epoch ms when the current preview started; 0 when idle. */
  startedAt: number;
  /** Synthetic sgv used for the preview, in mg/dL. Caller-supplied so
   *  the value is computed against whatever thresholds were in effect
   *  at the moment the user tapped (see `sgvForZone` below). */
  sgv: number | null;
}

/** The single idle snapshot. Returned BY REFERENCE from
 *  `getPreviewState()` so `useSyncExternalStore` can bail out on identity
 *  (a fresh object per call would re-render forever). */
const IDLE: PreviewState = { zone: null, startedAt: 0, sgv: null };

let state: PreviewState = IDLE;
let timer: number | null = null;

function notify(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(EVENT));
}

function clearTimer(): void {
  if (timer != null && typeof window !== "undefined") {
    window.clearTimeout(timer);
  }
  timer = null;
}

/** Compute the synthetic mg/dL value for a preview zone using the
 *  user's live thresholds. Pure function — called by the Settings
 *  button right before `startPreview` so the preview reflects the
 *  thresholds that were in effect at tap-time. Subsequent threshold
 *  edits during the 5-second window don't mutate the preview, matching
 *  how a real reading at that instant would behave. */
export function sgvForZone(zone: PreviewZone, t: Thresholds): number {
  switch (zone) {
    case "urgent_low":
      return Math.max(20, t.urgent_low - 10);
    case "low":
      return Math.round((t.urgent_low + t.target_low) / 2);
    case "in_range":
      return Math.round((t.target_low + t.target_high) / 2);
    case "high":
      return Math.round((t.target_high + t.urgent_high) / 2);
    case "urgent_high":
      return Math.min(600, t.urgent_high + 30);
  }
}

export function startPreview(zone: PreviewZone, sgv: number): void {
  clearTimer();
  state = { zone, startedAt: Date.now(), sgv };
  notify();
  if (typeof window === "undefined") return;
  // Cycle 351: the timer is now only a NOTIFICATION mechanism (so the
  // overlay/countdown re-render the moment the window closes). It is no
  // longer trusted for correctness — `getPreviewState()` enforces the
  // deadline on the wall clock. See the expiry note there.
  timer = window.setTimeout(() => {
    state = IDLE;
    timer = null;
    notify();
  }, DURATION_MS);
}

export function cancelPreview(): void {
  if (state.zone == null && timer == null) return;
  clearTimer();
  state = IDLE;
  notify();
}

/** Cycle 351 (SAFETY) — expiry is a WALL-CLOCK DEADLINE evaluated on
 *  every read, never a timer.
 *
 *  The 2026-07-19 05:53 incident: the app was opened and sounded the
 *  member's real high alarm sound while their live reading was 110 and in
 *  range — no alert row, no prediction, no push existed for that member
 *  that day. The Cycle-36 audio bridge in `App.tsx` calls
 *  `alarms.playHigh()` directly off `preview.zone`, with none of the
 *  freshness / server-classification / live-event gating that guards every
 *  server-driven cue (cycles 112 / 231 / 232 / 309). The only thing
 *  standing between a previewed "high" and a false alarm was this 5 s
 *  `setTimeout` — and iOS WKWebView / Android WebView SUSPEND JS timers
 *  while backgrounded (the same webview behavior cycle 309 documented for
 *  the auto-announce cadence). Background the app inside the preview
 *  window and the timeout never fires: `state.zone` stayed "high" for
 *  hours, and the bridge replayed the high sound on the next foreground.
 *
 *  Reading the deadline here makes a swallowed timer harmless: every
 *  consumer (audio bridge, PreviewOverlay, Settings countdown) already
 *  funnels through this one accessor. */
export function getPreviewState(): PreviewState {
  if (state.zone != null && Date.now() - state.startedAt >= DURATION_MS) {
    // Lazily collapse to idle. Pure w.r.t. observable behavior — the
    // caller would have been handed an expired preview otherwise.
    clearTimer();
    state = IDLE;
  }
  return state;
}

export function subscribePreview(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = () => listener();
  window.addEventListener(EVENT, handler);
  return () => window.removeEventListener(EVENT, handler);
}

/** Hardcoded preview window, exposed for the countdown badge so the
 *  overlay + Settings button agree on the same `Ns` value. */
export const PREVIEW_DURATION_MS = DURATION_MS;
